// ============================================================================
// gamificationRegistry — baseline definitions + PURE progression math
// ----------------------------------------------------------------------------
// Everything here is firebase-free and deterministic so it can be unit-tested
// in isolation. `gamificationService.ts` orchestrates these helpers against
// Firestore (transactions, caps, resets). Numbers the spec leaves open (XP
// curve, XP-per-event, tier→ticket mapping, daily credit cap) are flagged
// `TUNABLE` so a later product pass can adjust them without touching logic.
// ============================================================================

import type {
  ActivityTrackId,
  CosmeticRarity,
  SpinTier,
  TrackId,
  UserGamificationProfile,
} from '../types/schema'

export type RandomSource = () => number

const MS_PER_DAY = 86_400_000

// ---------------------------------------------------------------------------
// A. Activity tracks (four-tier "Fog of War" ladders)
// ---------------------------------------------------------------------------

/**
 * Presentational archetype for a track. Used by the Achievements UI to pick
 * an accent color + emblem; logic-free so tests and services can ignore it.
 */
export type TrackArchetype =
  | 'visionary'
  | 'strategist'
  | 'detailer'
  | 'tactician'
  | 'finisher'
  | 'closer'
  | 'grinder'
  | 'perfectionist'
  | 'spark'
  | 'refiner'
  | 'ascetic'
  | 'gambler'
  | 'whale'
  | 'cosmic'

export interface ActivityTrackDefinition {
  id: ActivityTrackId
  title: string
  unitLabel: string
  /** Ascending tier thresholds for levels 1..4. */
  tiers: readonly [number, number, number, number]
  /** UI-only archetype key. Maps to --track-<archetype> in gamification-tokens.css. */
  archetype: TrackArchetype
  /** Icon slug consumed by a page-local icon map (keeps this file SSR-safe). */
  iconKey:
    | 'flag'
    | 'target'
    | 'branch'
    | 'checkbox'
    | 'trophy'
    | 'check-circle'
    | 'workspace'
    | 'stars'
    | 'ai-spark'
    | 'ai-refine'
    | 'focus'
    | 'lucky'
    | 'mega'
    | 'giga'
}

export const ACTIVITY_TRACKS: Record<ActivityTrackId, ActivityTrackDefinition> = {
  TRACK_CREATE_GOAL: { id: 'TRACK_CREATE_GOAL', title: 'The Visionary', unitLabel: 'Created', tiers: [1, 50, 100, 200], archetype: 'visionary', iconKey: 'flag' },
  TRACK_CREATE_TASK: { id: 'TRACK_CREATE_TASK', title: 'The Strategist', unitLabel: 'Created', tiers: [1, 50, 100, 200], archetype: 'strategist', iconKey: 'target' },
  TRACK_CREATE_SUB: { id: 'TRACK_CREATE_SUB', title: 'The Detailer', unitLabel: 'Created', tiers: [1, 50, 100, 200], archetype: 'detailer', iconKey: 'branch' },
  TRACK_CREATE_CHECK: { id: 'TRACK_CREATE_CHECK', title: 'The Tactician', unitLabel: 'Created', tiers: [1, 50, 100, 200], archetype: 'tactician', iconKey: 'checkbox' },
  TRACK_FINISH_GOAL: { id: 'TRACK_FINISH_GOAL', title: 'The Finisher', unitLabel: 'Completed', tiers: [1, 50, 100, 200], archetype: 'finisher', iconKey: 'trophy' },
  TRACK_FINISH_TASK: { id: 'TRACK_FINISH_TASK', title: 'The Closer', unitLabel: 'Completed', tiers: [1, 50, 100, 200], archetype: 'closer', iconKey: 'check-circle' },
  TRACK_FINISH_SUB: { id: 'TRACK_FINISH_SUB', title: 'The Grinder', unitLabel: 'Completed', tiers: [1, 50, 100, 200], archetype: 'grinder', iconKey: 'workspace' },
  TRACK_FINISH_CHECK: { id: 'TRACK_FINISH_CHECK', title: 'The Perfectionist', unitLabel: 'Completed', tiers: [1, 50, 100, 200], archetype: 'perfectionist', iconKey: 'stars' },
  TRACK_AI_SPAWN: { id: 'TRACK_AI_SPAWN', title: 'The Spark', unitLabel: 'Essay Spawns', tiers: [1, 10, 50, 150], archetype: 'spark', iconKey: 'ai-spark' },
  TRACK_AI_REFINE: { id: 'TRACK_AI_REFINE', title: 'The Refiner', unitLabel: 'Prompt Edits', tiers: [1, 20, 100, 300], archetype: 'refiner', iconKey: 'ai-refine' },
  TRACK_FOCUS_TIME: { id: 'TRACK_FOCUS_TIME', title: 'The Ascetic', unitLabel: 'Locked Focus Mins', tiers: [25, 500, 2500, 10000], archetype: 'ascetic', iconKey: 'focus' },
  TRACK_LUCKY_SPINS: { id: 'TRACK_LUCKY_SPINS', title: 'The Gambler', unitLabel: 'Lucky Spins Done', tiers: [1, 10, 50, 150], archetype: 'gambler', iconKey: 'lucky' },
  TRACK_MEGA_SPINS: { id: 'TRACK_MEGA_SPINS', title: 'The Whale', unitLabel: 'Mega Spins Done', tiers: [1, 10, 50, 150], archetype: 'whale', iconKey: 'mega' },
  TRACK_GIGA_SPINS: { id: 'TRACK_GIGA_SPINS', title: 'The Cosmic', unitLabel: 'Giga Spins Done', tiers: [1, 10, 50, 150], archetype: 'cosmic', iconKey: 'giga' },
}

/**
 * Spin tier → rarity, used by the vault UI to reach for the rarity tokens.
 * Kept next to SPIN_TIERS so future tiers stay obvious.
 */
export const SPIN_TIER_RARITY: Record<SpinTier, CosmeticRarity> = {
  LUCKY_SPIN: 'RARE',
  MEGA_SPIN: 'EPIC',
  GIGA_SPIN: 'LEGENDARY',
}

export const ACTIVITY_TRACK_IDS = Object.keys(ACTIVITY_TRACKS) as ActivityTrackId[]

/** Highest tier (1..4) reached for a count; 0 when still below tier 1. */
export const resolveTrackLevel = (count: number, tiers: readonly number[]): number => {
  let level = 0
  for (let i = 0; i < tiers.length; i += 1) {
    if (count >= tiers[i]) level = i + 1
  }
  return level
}

/**
 * Which spin ticket each activity tier (1..4) deposits the first time it is
 * reached. TUNABLE — deeper tiers grant rarer tickets by default.
 */
export const ACTIVITY_TIER_TICKET_REWARDS: Record<number, SpinTier> = {
  1: 'LUCKY_SPIN',
  2: 'LUCKY_SPIN',
  3: 'MEGA_SPIN',
  4: 'GIGA_SPIN',
}

/**
 * Per-track override of the tier→ticket map. The spin-count ladders (TRACK_
 * LUCKY/MEGA/GIGA_SPINS) reward their OWN tier at every tier crossed so the
 * loop "spin more → earn tickets of a higher tier" stays bounded.
 */
export const TRACK_TIER_TICKET_REWARDS: Partial<Record<ActivityTrackId, Record<number, SpinTier>>> = {
  TRACK_LUCKY_SPINS: { 1: 'LUCKY_SPIN', 2: 'LUCKY_SPIN', 3: 'LUCKY_SPIN', 4: 'LUCKY_SPIN' },
  TRACK_MEGA_SPINS: { 1: 'MEGA_SPIN', 2: 'MEGA_SPIN', 3: 'MEGA_SPIN', 4: 'MEGA_SPIN' },
  TRACK_GIGA_SPINS: { 1: 'GIGA_SPIN', 2: 'GIGA_SPIN', 3: 'GIGA_SPIN', 4: 'GIGA_SPIN' },
}

/** Resolves the ticket tier granted when `trackId` crosses tier `tierNo`. */
export const resolveTierTicketReward = (
  trackId: ActivityTrackId,
  tierNo: number,
): SpinTier | undefined => {
  const override = TRACK_TIER_TICKET_REWARDS[trackId]
  if (override && override[tierNo]) return override[tierNo]
  return ACTIVITY_TIER_TICKET_REWARDS[tierNo]
}

// ---------------------------------------------------------------------------
// B. Login retention ladders
// ---------------------------------------------------------------------------

export const LOGIN_TOTAL_MILESTONES: readonly number[] = [1, 25, 50, 100, 150, 200]
export const LOGIN_STREAK_MILESTONES: readonly number[] = [10, 25, 50, 100]

// Wave 7+ — orbit skin unlocks by achievement, mirroring LOGIN_STREAK_REWARD_COSMETICS.
export const LEVEL_REWARD_ORBIT_SKINS: Readonly<Record<number, string>> = {
  3: 'orbit_ivory',
  6: 'orbit_ink',
  10: 'orbit_outline',
} as const

// Glass unlocks via streak (parallel achievement path so at least one skin doesn't require level grinding).
export const LOGIN_STREAK_REWARD_ORBIT_SKINS: Readonly<Record<number, string>> = {
  25: 'orbit_glass',
} as const

// TUNABLE — retention ticket payouts.
export const LOGIN_TOTAL_TICKET_REWARD: SpinTier = 'MEGA_SPIN'
export const LOGIN_STREAK_TICKET_REWARD: SpinTier = 'GIGA_SPIN'

/** Milestones strictly above `prevCount` and at or below `newCount`. */
export const crossedMilestones = (
  prevCount: number,
  newCount: number,
  milestones: readonly number[],
): number[] => milestones.filter((m) => m > prevCount && m <= newCount)

// ---------------------------------------------------------------------------
// C. Streak rescue
// ---------------------------------------------------------------------------

export const STREAK_RESCUE_AD_COUNT = 3
const STREAK_RESCUE_CREDIT_PER_DAY = 2
const STREAK_RESCUE_DAY_CAP = 50

/** Rescue Cost = min(ActiveStreakDays, 50) × 2 Credits. */
export const streakRescueCreditCost = (activeStreakDays: number): number =>
  Math.min(Math.max(activeStreakDays, 0), STREAK_RESCUE_DAY_CAP) * STREAK_RESCUE_CREDIT_PER_DAY

// ---------------------------------------------------------------------------
// D. Spin wheel economy
// ---------------------------------------------------------------------------

export type TicketField = 'luckySpinTickets' | 'megaSpinTickets' | 'gigaSpinTickets'
export type DailySpinCounterField =
  | 'dailyLuckySpinsExecuted'
  | 'dailyMegaSpinsExecuted'
  | 'dailyGigaSpinsExecuted'

export interface SpinTierConfig {
  tier: SpinTier
  minCredits: number
  maxCredits: number
  dailyCeiling: number
  ticketField: TicketField
  dailyCounterField: DailySpinCounterField
}

export const SPIN_TIERS: Record<SpinTier, SpinTierConfig> = {
  LUCKY_SPIN: {
    tier: 'LUCKY_SPIN',
    minCredits: 2,
    maxCredits: 10,
    dailyCeiling: 20,
    ticketField: 'luckySpinTickets',
    dailyCounterField: 'dailyLuckySpinsExecuted',
  },
  MEGA_SPIN: {
    tier: 'MEGA_SPIN',
    minCredits: 10,
    maxCredits: 50,
    dailyCeiling: 5,
    ticketField: 'megaSpinTickets',
    dailyCounterField: 'dailyMegaSpinsExecuted',
  },
  GIGA_SPIN: {
    tier: 'GIGA_SPIN',
    minCredits: 50,
    maxCredits: 150,
    dailyCeiling: 1,
    ticketField: 'gigaSpinTickets',
    dailyCounterField: 'dailyGigaSpinsExecuted',
  },
}

/** Uniform integer credit drop within the tier range (inclusive of both ends). */
export const rollSpinReward = (tier: SpinTier, rng: RandomSource = Math.random): number => {
  const { minCredits, maxCredits } = SPIN_TIERS[tier]
  const span = maxCredits - minCredits + 1
  const sample = Number(rng())
  const boundedSample = Number.isFinite(sample) ? Math.min(Math.max(sample, 0), 1 - Number.EPSILON) : 0
  return minCredits + Math.floor(boundedSample * span)
}

// TUNABLE — soft daily ceiling on credits bankable from spins/activity.
export const DAILY_ACTIVITY_CREDIT_CAP = 500

// ---------------------------------------------------------------------------
// E. Masked ad multiplier journey
// ---------------------------------------------------------------------------

export const DOUBLE_REWARD_MULTIPLIER = 2

/**
 * Number of sequential ads required to double a base payout.
 *   0–20 → 1 · 21–60 → 2 · 61–150 → 3 · 151+ or rare drop → 4
 */
export const adsRequiredToDouble = (baseValue: number, isRareDrop = false): number => {
  if (isRareDrop || baseValue >= 151) return 4
  if (baseValue >= 61) return 3
  if (baseValue >= 21) return 2
  return 1
}

// ---------------------------------------------------------------------------
// F. XP curve (TUNABLE — spec defines the fields, not the numbers)
// ---------------------------------------------------------------------------

export const XP_BASE_PER_LEVEL = 100

/** XP required to advance FROM `level` to `level + 1`. */
export const xpNeededForLevel = (level: number): number =>
  XP_BASE_PER_LEVEL * Math.max(1, Math.floor(level))

/** XP granted per progression event. For focus time this is per minute. */
export const XP_REWARDS: Record<TrackId, number> = {
  TRACK_CREATE_GOAL: 20,
  TRACK_CREATE_TASK: 10,
  TRACK_CREATE_SUB: 5,
  TRACK_CREATE_CHECK: 2,
  TRACK_FINISH_GOAL: 40,
  TRACK_FINISH_TASK: 20,
  TRACK_FINISH_SUB: 10,
  TRACK_FINISH_CHECK: 4,
  TRACK_AI_SPAWN: 15,
  TRACK_AI_REFINE: 8,
  TRACK_FOCUS_TIME: 1,
  TRACK_LUCKY_SPINS: 5,
  TRACK_MEGA_SPINS: 10,
  TRACK_GIGA_SPINS: 20,
  TRACK_TOTAL_LOGINS: 10,
  TRACK_STREAK_LOGINS: 25,
}

export interface XpApplication {
  level: number
  currentXp: number
  xpNeededForNextLevel: number
  levelsGained: number
}

/** Applies XP gain, rolling over as many level-ups as the surplus allows. */
export const applyXp = (level: number, currentXp: number, gainedXp: number): XpApplication => {
  let nextLevel = Math.max(1, Math.floor(level))
  let xp = Math.max(0, currentXp) + Math.max(0, gainedXp)
  let needed = xpNeededForLevel(nextLevel)
  let levelsGained = 0

  while (xp >= needed) {
    xp -= needed
    nextLevel += 1
    levelsGained += 1
    needed = xpNeededForLevel(nextLevel)
  }

  return { level: nextLevel, currentXp: xp, xpNeededForNextLevel: needed, levelsGained }
}

// ---------------------------------------------------------------------------
// G. Cosmetics + focus guardians
// ---------------------------------------------------------------------------

export const RARITY_BORDER_COLORS: Record<CosmeticRarity, string> = {
  COMMON: 'var(--rarity-common)',
  RARE: 'var(--rarity-rare)',
  EPIC: 'var(--rarity-epic)',
  LEGENDARY: 'var(--rarity-legendary)',
}

export type FocusParticleKind = 'NONE' | 'CHARCOAL_SMOKE' | 'LIGHT_WAVES'

export interface FocusGuardianConfig {
  id: string
  displayName: string
  rarity: CosmeticRarity
  particle: FocusParticleKind
  audioId: string
  audioLabel: string
}

export const FOCUS_GUARDIANS: Record<string, FocusGuardianConfig> = {
  ALCHEMIST: {
    id: 'ALCHEMIST',
    displayName: 'The Alchemist',
    rarity: 'EPIC',
    particle: 'CHARCOAL_SMOKE',
    audioId: 'lofi_rain_vinyl',
    audioLabel: 'Lo-Fi Heavy Rain + Vinyl Surface Crackle',
  },
  ZEN_MASTER: {
    id: 'ZEN_MASTER',
    displayName: 'The Zen Master',
    rarity: 'LEGENDARY',
    particle: 'LIGHT_WAVES',
    audioId: 'alpha_singing_bowl',
    audioLabel: 'Alpha-Wave Pad + High-Resonance Singing Bowl',
  },
}

// ---------------------------------------------------------------------------
// H. Raid combat constants
// ---------------------------------------------------------------------------

export const RAID_DAMAGE = {
  CHECKLIST_COMPLETE: 5,
  TASK_COMPLETE: 80,
  FOCUS_BLOCK: 120,
} as const

export const PENALTY_RAID_REWARD_MULTIPLIER = 1.5
export const BOSS_CURSE_DURATION_HOURS = 24
export const BOSS_CURSE_BREAK_CREDIT_COST = 50

/**
 * Total Boss HP = (Nested Tasks Count × Weight Mass × 10)
 *              + (Nested Checklist Nodes Count × 5)
 *
 * `weightMass` is the per-task weight multiplier from the spec formula; the
 * Phase 3 raid setup decides how it is aggregated from the target cluster.
 */
export const computeBossHp = (
  nestedTaskCount: number,
  weightMass: number,
  nestedChecklistCount: number,
): number =>
  Math.max(0, nestedTaskCount) * Math.max(0, weightMass) * 10 +
  Math.max(0, nestedChecklistCount) * 5

// ---------------------------------------------------------------------------
// I. Pure profile + reset + login math (orchestrated by the service)
// ---------------------------------------------------------------------------

/** UTC calendar-day key (`YYYY-MM-DD`) derived from an ISO timestamp. */
export const dayKey = (iso: string): string => iso.slice(0, 10)

const daysBetweenDayKeys = (fromDayKey: string, toDayKey: string): number => {
  const from = Date.parse(`${fromDayKey}T00:00:00Z`)
  const to = Date.parse(`${toDayKey}T00:00:00Z`)
  if (Number.isNaN(from) || Number.isNaN(to)) return Number.POSITIVE_INFINITY
  return Math.round((to - from) / MS_PER_DAY)
}

export const createDefaultGamificationProfile = (
  uid: string,
  nowIso: string,
): UserGamificationProfile => ({
  uid,
  level: 1,
  currentXp: 0,
  xpNeededForNextLevel: xpNeededForLevel(1),
  luckySpinTickets: 0,
  megaSpinTickets: 0,
  gigaSpinTickets: 0,
  lastMidnightResetTimestamp: nowIso,
  lastActiveLoginTimestamp: nowIso,
  dailyCreditsEarnedFromActivity: 0,
  dailyLuckySpinsExecuted: 0,
  dailyMegaSpinsExecuted: 0,
  dailyGigaSpinsExecuted: 0,
  cumulativeLoginsCount: 0,
  consecutiveLoginStreak: 0,
  equippedAvatarId: 'avatar_seedling',
  equippedTitle: 'Aspiring of the Timeline',
  equippedCanvasBgId: 'bg_grid',
  equippedThemeId: '',
  equippedAudioId: 'audio_silence',
  equippedOrbitSkinId: 'orbit_cream',
  bioMarkdown: '',
  pinnedBadgeIds: [],
  pinnedTimelineElementIds: [],
  unlockedAvatarIds: [],
  unlockedThemeIds: [],
  unlockedBackgroundIds: [],
  unlockedOrbitSkinIds: ['orbit_cream'],
  unlockedTitlePrefixes: [],
  unlockedTitleSuffixes: [],
  trackProgress: {},
  unclaimedTrackTickets: {},
})

export const isMidnightResetDue = (lastResetIso: string, nowIso: string): boolean =>
  dayKey(lastResetIso) !== dayKey(nowIso)

/**
 * Patch that zeroes the daily accumulators when a new cloud-day has begun.
 * Empty object when no reset is due, so it is safe to spread unconditionally.
 */
export const applyMidnightReset = (
  profile: UserGamificationProfile,
  nowIso: string,
): Partial<UserGamificationProfile> => {
  if (!isMidnightResetDue(profile.lastMidnightResetTimestamp, nowIso)) return {}
  return {
    dailyCreditsEarnedFromActivity: 0,
    dailyLuckySpinsExecuted: 0,
    dailyMegaSpinsExecuted: 0,
    dailyGigaSpinsExecuted: 0,
    lastMidnightResetTimestamp: nowIso,
  }
}

export interface LoginUpdateResult {
  cumulativeLoginsCount: number
  consecutiveLoginStreak: number
  /** The streak value that was lost, before reset — drives rescue cost/UI. */
  brokenStreakValue: number
  streakBroken: boolean
  isFirstLoginOfDay: boolean
}

/**
 * Computes the next login/streak state from the stored profile.
 *  - Same cloud-day re-entry is a no-op (already counted).
 *  - Exactly one day gap extends the streak.
 *  - A gap > 1 day resets the streak to 1 and flags `streakBroken`, surfacing
 *    the lost value so the caller can offer the rescue path.
 */
export const computeLoginUpdate = (
  profile: UserGamificationProfile,
  nowIso: string,
): LoginUpdateResult => {
  const today = dayKey(nowIso)
  const lastDay = dayKey(profile.lastActiveLoginTimestamp)
  const hasPriorLogin = profile.cumulativeLoginsCount > 0

  if (hasPriorLogin && lastDay === today) {
    return {
      cumulativeLoginsCount: profile.cumulativeLoginsCount,
      consecutiveLoginStreak: profile.consecutiveLoginStreak,
      brokenStreakValue: 0,
      streakBroken: false,
      isFirstLoginOfDay: false,
    }
  }

  const gap = daysBetweenDayKeys(lastDay, today)
  const isConsecutive = hasPriorLogin && gap === 1
  const streakBroken = hasPriorLogin && gap > 1

  return {
    cumulativeLoginsCount: profile.cumulativeLoginsCount + 1,
    consecutiveLoginStreak: isConsecutive ? profile.consecutiveLoginStreak + 1 : 1,
    brokenStreakValue: streakBroken ? profile.consecutiveLoginStreak : 0,
    streakBroken,
    isFirstLoginOfDay: true,
  }
}
